import { useCallback } from "react";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select, { type SelectChangeEvent } from "@mui/material/Select";
import { Note } from "../../../../../components/primitives/data-display/Note";
import type { DocumentIndexingRefreshMode } from "../../../../../lib/corpusApi";
import {
  DOCUMENT_INDEXING_REFRESH_MODE_COPY,
  DOCUMENT_INDEXING_REFRESH_MODE_VALUES,
} from "./document_indexing_refresh_fields";
import type { UseDocumentIndexingRefreshParamsResult } from "./useDocumentIndexingRefreshParams";

export interface DocumentIndexingRefreshParamsFormProps {
  form: UseDocumentIndexingRefreshParamsResult;
}

/** The one param a document_indexing_refresh job asks for: whether to cover only the documents without
 *  fields or every document. */
export function DocumentIndexingRefreshParamsForm({
  form,
}: DocumentIndexingRefreshParamsFormProps) {
  const { mode, setMode } = form;

  const handleModeChange = useCallback(
    (e: SelectChangeEvent) => setMode(e.target.value as DocumentIndexingRefreshMode),
    [setMode]
  );

  return (
    <>
      <FormControl fullWidth size="small">
        <InputLabel id="document-indexing-refresh-mode-label">Documents</InputLabel>
        <Select
          labelId="document-indexing-refresh-mode-label"
          label="Documents"
          value={mode}
          onChange={handleModeChange}
        >
          {DOCUMENT_INDEXING_REFRESH_MODE_VALUES.map((value) => (
            <MenuItem key={value} value={value}>
              {DOCUMENT_INDEXING_REFRESH_MODE_COPY[value].label}
            </MenuItem>
          ))}
        </Select>
        <FormHelperText>{DOCUMENT_INDEXING_REFRESH_MODE_COPY[mode].help}</FormHelperText>
      </FormControl>
      <Note>
        Refreshes how each document is indexed: its fields, subjects, main subjects and publication
        types. Fields and subjects are only ever added, never removed, main subjects and publication
        types are replaced when the source holds new ones, and no scorecard field is touched.
        Nothing waits for review. Documents that still have no fields afterwards are listed in the
        job&apos;s results.
      </Note>
    </>
  );
}
